import React from "react";
import { Box, Typography, Paper, Button, Divider } from "@material-ui/core";
import styles from "./RouteLoadSheetContent.module.css";

const formatCurrency = (value) => {
  if (value === null || value === undefined) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const getInvoiceAmount = (invoice) =>
  invoice?.total ?? invoice?.subTotal ?? invoice?.amount ?? null;

const getInvoiceName = (invoice) =>
  invoice?.client?.store ||
  invoice?.client?.name ||
  "Invoice";

const getInvoiceQuantity = (invoice) =>
  (invoice?.items || []).reduce(
    (sum, item) => sum + (Number(item?.quantity) || 0),
    0
  );

const getProductBreakdown = (stops = []) => {
  const map = new Map();
  stops.forEach((stop) => {
    const invoice = typeof stop.invoice === "object" ? stop.invoice : null;
    (invoice?.items || []).forEach((item) => {
      const label = item?.modelNumber || item?.name || item?.productName || "Item";
      const qty = Number(item?.quantity) || 0;
      if (!qty) return;
      map.set(label, (map.get(label) || 0) + qty);
    });
  });
  return Array.from(map.entries())
    .map(([label, qty]) => ({ label, qty }))
    .sort((a, b) => b.qty - a.qty);
};

export default function RouteLoadSheetContent({
  plan,
  showPrintButton = false,
  className = "",
  pageBreakAfter = false,
}) {
  const stops = plan?.stops || [];
  const totalStops = stops.length;
  const totalValue =
    stops.reduce((sum, stop) => {
      const invoice = typeof stop.invoice === "object" ? stop.invoice : null;
      const amount = getInvoiceAmount(invoice);
      return sum + (Number(amount) || 0);
    }, 0) || 0;
  const totalQuantity =
    stops.reduce((sum, stop) => {
      const invoice = typeof stop.invoice === "object" ? stop.invoice : null;
      return sum + getInvoiceQuantity(invoice);
    }, 0) || 0;
  const productBreakdown = React.useMemo(
    () => getProductBreakdown(stops),
    [stops]
  );

  if (!plan?._id) return null;

  return (
    <Paper
      className={`${styles.sheet} route-printable-sheet ${className} ${
        pageBreakAfter ? styles.pageBreak : ""
      }`}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <div>
          <Typography variant="h4" gutterBottom>
            {plan.name || "Route Load Sheet"}
          </Typography>
          <Typography color="textSecondary">
            Route Date: {formatDate(plan.routeDate)}
          </Typography>
          <Typography color="textSecondary">
            Driver: {plan.driverName || "—"}
          </Typography>
          <Typography color="textSecondary">
            Status: {(plan.status || "planned").toUpperCase()}
          </Typography>
        </div>
        <Box display="flex" flexDirection="column" alignItems="flex-end">
          <Typography variant="h6">{formatCurrency(totalValue)}</Typography>
          <Typography color="textSecondary">Total Load Value</Typography>
          <Typography variant="subtitle1" style={{ marginTop: 8, fontWeight: 600 }}>
            {totalStops} stop{totalStops === 1 ? "" : "s"}
          </Typography>
          <Typography color="textSecondary">
            Total Quantity: {totalQuantity}
          </Typography>
          {showPrintButton && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => window.print()}
              className={styles.printButton}
            >
              Print
            </Button>
          )}
        </Box>
      </Box>

      {plan.notes && (
        <Box mt={3} className={styles.notesBlock}>
          <Typography variant="subtitle1">Notes</Typography>
          <Typography color="textSecondary">{plan.notes}</Typography>
        </Box>
      )}

      {!!productBreakdown.length && (
        <>
          <Divider style={{ margin: "24px 0" }} />
          <Box>
            <Typography variant="h6" gutterBottom>
              Item Breakdown
            </Typography>
            <Box component="ul" className={styles.breakdownList}>
              {productBreakdown.map((item) => (
                <li key={item.label}>
                  <Typography variant="body2">
                    {item.label}: <strong>{item.qty}</strong>
                  </Typography>
                </li>
              ))}
            </Box>
          </Box>
        </>
      )}

      <Divider style={{ margin: "24px 0" }} />

      <Box>
        <Typography variant="h6" gutterBottom>
          Stops
        </Typography>
        {!plan.stops?.length ? (
          <Typography color="textSecondary">
            No stops assigned to this route.
          </Typography>
        ) : (
          plan.stops.map((stop, index) => {
            const invoice = typeof stop.invoice === "object" ? stop.invoice : null;
            const amount = getInvoiceAmount(invoice);
            const quantity = getInvoiceQuantity(invoice);
            const breakdown = getProductBreakdown([stop]);
            return (
              <Box key={stop._id || `${plan._id}-${index}`} className={styles.stopCard}>
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                  {index + 1}. {getInvoiceName(invoice)}
                </Typography>
                <Typography color="textSecondary">
                  Address: {invoice?.client?.address || "—"}
                </Typography>
                <Typography color="textSecondary">
                  Amount: {formatCurrency(amount)}
                </Typography>
                <Typography color="textSecondary">
                  Quantity: {quantity || "—"}
                </Typography>
                {!!breakdown.length && (
                  <Typography component="div" style={{ fontWeight: 600, color: "#000" }}>
                    {breakdown.map((item) => `${item.qty} × ${item.label}`).join(" • ")}
                  </Typography>
                )}
                <Typography color="textSecondary">
                  Due: {formatDate(invoice?.dueDate)}
                </Typography>
                {stop.notes && (
                  <Typography color="textSecondary">Notes: {stop.notes}</Typography>
                )}
                <Typography color="textSecondary">
                  Status: {(stop.status || "pending").toUpperCase()}
                </Typography>
              </Box>
            );
          })
        )}
      </Box>
    </Paper>
  );
}

export { formatDate, formatCurrency };
